// @ts-check
import { extend, h, uid } from "./dom.js";

/**
 * Tabs (DS-13) following the ARIA tabs pattern with automatic activation. Pages
 * link tabs to the URL through `onChange`; panels are optional so a router can
 * render the selected section itself.
 *
 * @param {{ label: string, items: { id: string, text: string, panel?: HTMLElement, count?: Node }[], value: string, onChange?: (id: string) => void }} options
 */
export function tabs({ label, items, value, onChange }) {
  const list = h("div", { class: "ui-tabs", role: "tablist", aria: { label } });
  const prefix = uid("tab");
  /** @type {HTMLButtonElement[]} */
  const buttons = items.map((item) => {
    const tab = h(
      "button",
      {
        type: "button",
        role: "tab",
        id: `${prefix}-${item.id}`,
        class: "ui-tabs__tab",
        dataset: { tab: item.id },
        onClick: () => select(item.id, true),
      },
      h("span", { text: item.text }),
      item.count,
    );
    if (item.panel) {
      item.panel.setAttribute("role", "tabpanel");
      item.panel.setAttribute("aria-labelledby", tab.id);
      if (!item.panel.id) item.panel.id = `${tab.id}-panel`;
      tab.setAttribute("aria-controls", item.panel.id);
    }
    return tab;
  });
  list.append(...buttons);
  let current = value;
  function render() {
    for (const [index, tab] of buttons.entries()) {
      const on = tab.dataset.tab === current;
      tab.setAttribute("aria-selected", String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = items[index].panel;
      if (panel) panel.hidden = !on;
    }
  }
  /** @param {string} id @param {boolean} notify */
  function select(id, notify) {
    if (!items.some((item) => item.id === id)) return;
    const changed = id !== current;
    current = id;
    render();
    if (notify && changed) onChange?.(id);
  }
  list.addEventListener("keydown", (event) => {
    const index = buttons.findIndex((tab) => tab.dataset.tab === current);
    const target =
      event.key === "ArrowRight"
        ? (index + 1) % buttons.length
        : event.key === "ArrowLeft"
          ? (index - 1 + buttons.length) % buttons.length
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? buttons.length - 1
              : -1;
    if (target < 0) return;
    event.preventDefault();
    select(String(buttons[target].dataset.tab), true);
    buttons[target].focus();
  });
  render();
  return extend(list, {
    get value() {
      return current;
    },
    set value(id) {
      select(id, false);
    },
    /** @param {string} id */
    setValue(id) {
      select(id, false);
    },
  });
}
